/**
 * Designation Task Service
 * Handles designation task management operations
 * Microservice: departmentmanagement
 * Domain: departments
 */
import { BaseService } from '../base/BaseService';
export class DesignationTaskService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? DesignationTaskService.DEFAULT_SERVICE_PATH,
            basePath: DesignationTaskService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all designation tasks with optional filters
     * @param isActive - Optional filter for active/inactive tasks
     * @param designationId - Optional filter by designation ID
     * @returns Array of designation tasks
     */
    async getAll(isActive, designationId) {
        const response = await this.getAllRaw(isActive, designationId);
        return response.data ?? [];
    }
    /**
     * Get all designation tasks with optional filters (raw response)
     * @param isActive - Optional filter for active/inactive tasks
     * @param designationId - Optional filter by designation ID
     * @returns Raw response with array of designation tasks
     */
    getAllRaw(isActive, designationId) {
        const params = {};
        if (isActive !== undefined)
            params.isActive = isActive;
        if (designationId !== undefined)
            params.designationId = designationId;
        const url = this.buildUrlWithParams('', params);
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    /**
     * Get designation task by ID
     * @param id - Designation task ID
     * @returns Designation task details
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Designation task with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get designation task by ID (raw response)
     * @param id - Designation task ID
     * @returns Raw response with designation task details
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new designation task
     * @param data - Designation task creation data
     * @returns Created designation task
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create designation task');
        }
        return response.data;
    }
    /**
     * Create a new designation task (raw response)
     * @param data - Designation task creation data
     * @returns Raw response with created designation task
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing designation task
     * @param id - Designation task ID
     * @param data - Designation task update data
     * @returns Updated designation task
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update designation task with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing designation task (raw response)
     * @param id - Designation task ID
     * @param data - Designation task update data
     * @returns Raw response with updated designation task
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Soft delete a designation task
     * @param id - Designation task ID
     * @returns Success status
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Soft delete a designation task (raw response)
     * @param id - Designation task ID
     * @returns Raw response with success status
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== CACHE ====================
    /**
     * Invalidate designation task cache
     * @returns Success status
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate designation task cache (raw response)
     * @returns Raw response with success status
     */
    invalidateCacheRaw() {
        const url = this.buildUrl('cache/invalidate');
        return this.http.post(url, {});
    }
}
DesignationTaskService.DEFAULT_SERVICE_PATH = 'departments';
DesignationTaskService.BASE_PATH = 'api/designationtasks';
//# sourceMappingURL=DesignationTaskService.js.map